import {InfoPage} from '@raison/ui-kit';
import {useQuery} from '@tanstack/react-query';
import React, {useMemo} from 'react';
import {Navigate} from 'react-router';
import {isOfCryptoType, useCurrenciesInfoOptions} from '~/entities/currencies';
import {NotFoundPage} from '~/entities/fallbacks';
import {Decoder} from '@raison/prelude';
import {enrichTrtData, useTrTxDetailsOptions} from '../api/fetchTrTxDetails';
import {SendTrTxContinuation} from '../ui/SendTrTxContinuation';
import {TrTxPendingPage} from '../ui/TrTxPendingPage';
import {TrTxRejectedPage} from '../ui/TrTxRejectedPage';
import {useTxStatusSearchParam} from './SendTxRouting';
import {TrTxId} from './TrTx';

export function SendTrTxContinuationPageContainer({trtxId: maybeTrTxId}: {trtxId: string}): React.JSX.Element {
    const trtxId = useMemo(() => Decoder.run(TrTxId, maybeTrTxId).orThrow(), [maybeTrTxId]);

    const trtxQuery = useQuery({
        ...useTrTxDetailsOptions(trtxId),
        refetchOnMount: true,
    });
    const currenciesQuery = useQuery({
        ...useCurrenciesInfoOptions(),
        select: info => info.filter(isOfCryptoType),
    });

    const trtStatus = useTxStatusSearchParam();

    const gotError = trtxQuery.isError || currenciesQuery.isError;
    const refetch = () => {
        if (trtxQuery.isError) {
            trtxQuery.refetch();
        }
        if (currenciesQuery.isError) {
            currenciesQuery.refetch();
        }
    };
    const LoadingScreen = <InfoPage.Skeleton title={'Send crypto'} gotError={gotError} reset={refetch} />;

    if (trtxQuery.isLoading || currenciesQuery.isLoading || gotError) {
        return LoadingScreen;
    }
    if (!trtxQuery.data || !currenciesQuery.data) {
        return <NotFoundPage />;
    }
    const trt = enrichTrtData(trtxQuery.data, currenciesQuery.data);
    if (trtxQuery.isFetching && trt.status !== trtStatus) {
        return LoadingScreen;
    }
    switch (trt.status) {
        case 'pending':
            return <TrTxPendingPage trt={trt} />;
        case 'rejected':
            return <TrTxRejectedPage trt={trt} currency={trt.currency} operation='Send' />;
        case 'approved':
            return <SendTrTxContinuation trt={trt} />;
        case 'completed':
            return <Navigate replace to='/' />;
    }
}
